import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import _values from 'lodash/values';
import _has from 'lodash/has';
import _clone from 'lodash/clone';
import _merge from 'lodash/merge';
import _get from 'lodash/get';
import _toPairs from 'lodash/toPairs';
import _fromPairs from 'lodash/fromPairs';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import type { WSuiteDetailsCardView } from '@zola/svc-web-api-ts-client';
import MinimalLoader from 'components/common/ui/MinimalLoader';
import { getUserIsGuest, getUserContext } from 'selectors/user/userSelectors';
import { getUserContext as getUserContextAction } from 'actions/UserActions';
import { getMultiSampleState } from 'cards/selectors/samples';
import {
  extractUserWeddingDetails,
  parseWeddingDate,
  lookupDefaultVariation,
  getIsSampleCustomizable,
  createSubstitutionsFromObjects,
  lookupSelectedVariation,
} from 'cards/components/common/Samples/util/helpers';
import { useCardSuiteDetailsSet } from 'cards/components/common/Samples/util/hooks';
import { getLocationData } from 'cards/components/common/Samples/util/api';
import { hideModal as hideModalAction } from 'cards/actions/modalActions';
import { UnitedStates } from 'components/common/form/CountryCodes';

import { CardListing, CARD_TYPES } from 'cards/components/CardListing/types';
import { RootState, AppDispatch } from 'reducers/index';
import { MultiSampleState } from 'cards/components/CardListing/CardListingRedesign/CardListingFilter/FilterTypes';
import { checkFreeSampleAvailability as checkFreeSampleAvailabilityAction } from 'cards/actions/projectActions';
import { trackEvent } from '../../../../../util/trackingHelper';
import ApiService from '../../../../../util/api';
import Layout from '../../Samples/Layout';
import styles from './DigitalSTDFreeSampleModalStyles.module.less';

export type OrderPlacedMessageProps = {
  goToNextStep: () => void;
  imageSrc: string | null;
};

const OrderPlacedMessage = ({ goToNextStep, imageSrc }: OrderPlacedMessageProps): JSX.Element => (
  <>
    <Layout.Header>
      Success! We&lsquo;ll get those to you soon
      <span role="img" aria-label="love letter">
        💌
      </span>
    </Layout.Header>
    <Layout.Text>Your samples ship free and will arrive in 5–10 business days.</Layout.Text>
    <div className={styles.imageContainer}>
      <img src={`${imageSrc}?w=300`} alt="" />
    </div>
    <Layout.FormButtons
      backClassName={styles.hide}
      className={styles.formButtonsOverride}
      nextLabel="Got it"
      nextClassName={styles.nextOverride}
      onNext={() => goToNextStep()}
    />
  </>
);

export type CartNotEmptyMessageProps = {
  onClose: () => void;
};

const CartNotEmptyMessage = ({ onClose }: CartNotEmptyMessageProps): JSX.Element => {
  const goToCart = () => {
    window.location.href = '/checkout/cart';
  };

  return (
    <>
      <Layout.Header>Clear your cart to start customizing</Layout.Header>
      <Layout.Text>
        Your cart currently contains other items. Check out or remove them to proceed.
      </Layout.Text>
      <Layout.FormButtons
        className={styles.formButtonsOverride}
        backLabel="Cancel"
        nextLabel="See Cart"
        nextClassName={styles.nextOverride}
        onBack={() => onClose()}
        onNext={() => goToCart()}
      />
    </>
  );
};

export type FreeSampleErrorMessageProps = {
  goToNextStep: () => void;
  hideModal: () => void;
  message: string;
  cartNotEmpty: boolean;
};

const FreeSampleErrorMessage = ({
  goToNextStep,
  hideModal,
  message,
  cartNotEmpty,
}: FreeSampleErrorMessageProps): JSX.Element => {
  return (
    <>
      {cartNotEmpty && <CartNotEmptyMessage onClose={hideModal} />}
      {!cartNotEmpty && (
        <>
          <Layout.Header>{message}</Layout.Header>
          <Layout.FormButtons
            backClassName={styles.hide}
            className={styles.formButtonsOverride}
            nextLabel="Got It"
            nextClassName={styles.nextOverride}
            onNext={() => goToNextStep()}
          />
        </>
      )}
    </>
  );
};

const stateFilter = (option: typeof UnitedStates[0]) => !['AA', 'AE', 'AP'].includes(option.value);

type StateDropdownType = {
  label: string;
  value: string;
};

const StateDropdownOptions: StateDropdownType[] = UnitedStates.filter(stateFilter).map(option => ({
  label: option.title,
  value: option.value,
}));

const initialForm = {
  firstName: null,
  lastName: null,
  address: null,
  apt: null,
  city: null,
  state: null,
  zipCode: null,
  country_code: 'USA',
};

const FETCH_STATE = {
  INITIAL: 'INITIAL',
  LOADING: 'LOADING',
  LOADED: 'LOADED',
};

type OrderDetails = {
  id?: number;
  email?: string;
};

export type DigitalSTDFreeSampleModalProps = {
  isGuest: boolean;
  userContext: UserContext;
  hideModal: () => void;
  multiSampleState: MultiSampleState;
  goToNextStep: () => void;
  setModalOptions: (...args: unknown[]) => unknown;
  checkFreeSampleAvailability: (cardType: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackingParams?: any;
};

const DigitalSTDFreeSampleModal = (props: DigitalSTDFreeSampleModalProps): JSX.Element => {
  const isAdminView = false;
  const isMulti = true;

  const { isGuest, userContext, hideModal, multiSampleState, goToNextStep, trackingParams } = props;

  const [form, setFormState] = useState(initialForm);
  const [fetchedLocationData, setFetchedLocationData] = useState(FETCH_STATE.INITIAL);
  const [errors, setErrors] = useState({});

  const [cardSuites, setCardSuites] = useState<CardListing[]>([]);
  const [initialColorSet, setInitialColorSet] = useState<Record<string, string>>({});
  const [cardSuiteUUIDList, setCardSuiteUUIDList] = useState<string[] | null>(null);
  const [variationSet, setVariationSet] = useState<any>();

  const [completedOrder, setCompletedOrder] = useState<OrderDetails | null>(null);
  const [displayImage, setDisplayImage] = useState<string | null>(null);

  const [busy, setBusy] = useState<boolean>(false);

  const { firstName, lastName, address, apt, city, state, zipCode } = form;

  // Update form values with object patches e.g. updateForm({ shipping: { city: null, zipCode: '12345' } })
  const updateForm = useCallback(patch => setFormState(_clone(_merge(form, patch))), [
    form,
    setFormState,
  ]);

  const setField = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
    updateForm({ [key]: event.target.value });
  const setState = (option: StateDropdownType) => updateForm({ state: option });

  useEffect(() => {
    setCardSuites(_values(multiSampleState.selected));
  }, [multiSampleState.selected]);

  useEffect(() => {
    setInitialColorSet(
      _fromPairs(
        cardSuites.map(card => [card.suiteUUID, card.initialColor || card.colors[0].value])
      ) as Record<string, string>
    );
    setCardSuiteUUIDList(cardSuites.map(cardSuite => cardSuite.suiteUUID));
  }, [cardSuites]);

  // Prefill shipping information
  useEffect(() => {
    if (fetchedLocationData === FETCH_STATE.INITIAL) {
      setFetchedLocationData(FETCH_STATE.LOADING);

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getLocationData(userContext).then(locationData => {
        setFetchedLocationData(FETCH_STATE.LOADED);

        if (locationData && locationData.shippingAddress) {
          const shipping = locationData.shippingAddress;

          updateForm({
            firstName: shipping.first_name,
            lastName: shipping.last_name,
            address: shipping.street_address_1,
            apt: shipping.street_address_2,
            city: shipping.city,
            state: shipping.state_province_region,
            zipCode: shipping.postal_code,
          });
        }
      });
    }

    return () => {
      updateForm(initialForm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userContext, fetchedLocationData]);

  const cardSuiteDetailsSet: {
    [key: string]: WSuiteDetailsCardView;
  } = useCardSuiteDetailsSet(cardSuiteUUIDList, isAdminView, isMulti);

  useEffect(() => {
    setVariationSet(
      _fromPairs(
        _toPairs(cardSuiteDetailsSet).map(([key, suiteDetails]) => {
          const defaultVariation = lookupDefaultVariation(suiteDetails, false);
          const colorVariation = lookupSelectedVariation(
            {
              ...defaultVariation.option_values,
              color: initialColorSet[key],
            },
            suiteDetails,
            false
          );

          return [key, colorVariation || defaultVariation];
        })
      )
    );
  }, [cardSuiteDetailsSet, initialColorSet]);

  const placeOrder = () => {
    // Check if first name, last name are entered, since we dont check this in the backend
    if (!firstName) {
      setErrors({
        errors: {
          shipping: {
            field: {
              firstName: 'Please make sure you enter your first name',
            },
          },
        },
      });
      return;
    }
    if (!lastName) {
      setErrors({
        errors: {
          shipping: {
            field: {
              lastName: 'Please make sure you enter your last name',
            },
          },
        },
      });
      return;
    }

    setBusy(true);

    const userWeddingDetails = extractUserWeddingDetails(userContext);
    const weddingNames = {
      owner_first_name: userWeddingDetails.owner_first_name,
      owner_last_name: userWeddingDetails.owner_last_name,
      partner_first_name: userWeddingDetails.partner_first_name,
      partner_last_name: userWeddingDetails.partner_last_name,
    };

    const weddingDate = userWeddingDetails.wedding_date
      ? parseWeddingDate(userWeddingDetails.wedding_date)
      : null;

    const projects = cardSuiteUUIDList?.map((key: string) => {
      return {
        variation_uuid: variationSet[key].uuid,
        suite_uuid: key,
        sku_object_id: !getIsSampleCustomizable(cardSuiteDetailsSet[key])
          ? cardSuiteDetailsSet[key].single_sample_sku
          : null,
      };
    });

    // ignore typescript error until helpers.js is converted to typescript
    // @ts-ignore
    const substitutions = createSubstitutionsFromObjects(weddingNames, weddingDate);

    const cardInfo = {
      projects,
      wedding_details: {
        ...weddingNames,
        event_date: weddingDate,
        ...substitutions,
      },
    };

    const body = {
      ...cardInfo,
      card_type: CARD_TYPES.invitation,
      address_validation: {
        street_line_1: form.address,
        street_line_2: form.apt,
        city: form.city,
        state_province_region: form.state,
        postal_code: form.zipCode,
        country_code: form.country_code,
      },
    };

    const apiResponse = ApiService.post('/web-api/v1/card-single-sample/order', body)
      .then(data => {
        if (!data.addressValidated) {
          return {
            errors: {
              shipping: {
                field: {
                  address: 'Please check the address',
                  city: 'Please check the city',
                  state: 'Please check the state',
                  zipCode: 'Check zip code',
                },
              },
            },
          };
        }
        if (!data.checkedOut) {
          return {
            errors: {
              shipping: {
                form: {
                  cartNotEmpty: data.message === "User's cart is not empty",
                  general: "Something went wrong. Let's try that again.",
                  // Potential issues:
                  // "Failed during checkout"
                  // "Failed adding the sku to cart"
                  // "Failed adding the project to cart"
                  // "Failed during project creation"
                  // "Single sample is not available on all suites selected by user"
                },
              },
            },
          };
        }
        return { orderDetails: { id: data.orderNumber } };
      })
      .then(result => {
        if (result.errors) {
          return result;
        }

        return {
          orderDetails: { ...(result.orderDetails || {}), email: userContext.email },
        };
      })
      .then(response => {
        // @ts-ignore
        if (response.errors) {
          // @ts-ignore
          setErrors(response.errors);

          return null;
        }

        return response.orderDetails;
      });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    Promise.resolve(apiResponse).then(orderDetails => {
      trackEvent('Flow complete', trackingParams);
      setBusy(false);
      if (orderDetails) {
        const { setModalOptions, checkFreeSampleAvailability } = props;

        setCompletedOrder(orderDetails);
        checkFreeSampleAvailability('INVITATION');

        setModalOptions({
          closeCallback: () => {
            goToNextStep();
          },
        });

        const firstCardSuite = cardSuiteDetailsSet[Object.keys(cardSuiteDetailsSet)[0]];
        if (firstCardSuite.images_urls) {
          const firstCardColor = firstCardSuite.uuid && initialColorSet[firstCardSuite.uuid];
          const imageObject = firstCardColor
            ? firstCardSuite.images_urls[firstCardColor]
            : Object.values(firstCardSuite.images_urls)[0];
          setDisplayImage(imageObject.plp_front);
        }
      }
    });
  };

  const shouldShowForm =
    !completedOrder &&
    !_has(errors, ['shipping', 'form', 'cartNotEmpty']) &&
    !_has(errors, ['shipping', 'form', 'general']);

  return (
    <div className={styles.container}>
      <Layout.Row className={styles.loaderAnchor}>
        <Layout.Col>
          {busy && (
            <div className={styles.loaderContainer}>
              <MinimalLoader />
            </div>
          )}
          {!busy && (
            <>
              {completedOrder && (
                <OrderPlacedMessage goToNextStep={goToNextStep} imageSrc={displayImage} />
              )}

              {/* On general error, go to the next step when closing the modal */}
              {/* On cart error, remain on current step or go to cart */}
              {_has(errors, ['shipping', 'form', 'general']) && (
                <FreeSampleErrorMessage
                  goToNextStep={goToNextStep}
                  hideModal={hideModal}
                  message={_get(errors, ['shipping', 'form', 'general'])}
                  cartNotEmpty={_get(errors, ['shipping', 'form', 'cartNotEmpty'])}
                />
              )}

              {shouldShowForm && (
                <Layout.Form onSubmit={() => placeOrder()}>
                  <Layout.Header>Coming soon to a mailbox near you...</Layout.Header>
                  <Layout.Text className={styles.textOverride}>
                    <span className="text-danger">*</span> Required
                  </Layout.Text>
                  <Layout.InputGroup>
                    <Layout.Field
                      isRequired
                      type="text"
                      label="First name"
                      name="firstName"
                      value={firstName || ''}
                      errorMessage={_get(errors, ['shipping', 'field', 'firstName']) || null}
                      onChange={setField('firstName')}
                    />
                    <Layout.Spacer className={styles.spacerOverride} />
                    <Layout.Field
                      isRequired
                      type="text"
                      label="Last name"
                      name="lastName"
                      value={lastName || ''}
                      errorMessage={_get(errors, ['shipping', 'field', 'lastName']) || null}
                      onChange={setField('lastName')}
                    />
                  </Layout.InputGroup>
                  <Layout.InputGroup>
                    <Layout.Field
                      isRequired
                      type="text"
                      label="Street address"
                      name="address"
                      value={address || ''}
                      errorMessage={_get(errors, ['shipping', 'field', 'address']) || null}
                      onChange={setField('address')}
                    />
                    <Layout.Spacer className={styles.spacerOverride} />
                    <Layout.Field
                      small
                      type="text"
                      label="Apt, etc"
                      name="apt"
                      value={apt || ''}
                      onChange={setField('apt')}
                    />
                  </Layout.InputGroup>
                  <Layout.InputGroup>
                    <Layout.Field
                      className={styles.widthOverride}
                      isRequired
                      type="text"
                      label="City"
                      name="city"
                      value={city || ''}
                      errorMessage={_get(errors, ['shipping', 'field', 'city']) || null}
                      onChange={setField('city')}
                    />
                    <Layout.Spacer className={styles.spacerOverride} />
                    <Layout.InputGroup>
                      <Layout.Dropdown
                        className={styles.widthOverride}
                        isRequired
                        name="state"
                        label="State"
                        selected={state}
                        options={StateDropdownOptions}
                        onSelect={setState}
                        errorMessage={_get(errors, ['shipping', 'field', 'state']) || null}
                      />
                      <Layout.Spacer className={styles.spacerOverride} />
                      <Layout.Field
                        className={styles.widthOverride}
                        isRequired
                        type="text"
                        label="Zip code"
                        name="zipCode"
                        value={zipCode || ''}
                        errorMessage={_get(errors, ['shipping', 'field', 'zipCode']) || null}
                        onChange={setField('zipCode')}
                      />
                    </Layout.InputGroup>
                  </Layout.InputGroup>
                  <Layout.FormButtons
                    className={styles.formButtonsOverride}
                    backLabel="Cancel"
                    nextLabel={isGuest ? 'Next - Guest' : 'Order Samples'}
                    nextDisabled={
                      !address ||
                      !city ||
                      !state ||
                      !zipCode ||
                      !firstName ||
                      !lastName ||
                      !cardSuiteUUIDList ||
                      (cardSuiteUUIDList && cardSuiteUUIDList.length === 0) ||
                      !variationSet ||
                      (variationSet && Object.keys(variationSet).length === 0)
                    }
                    nextClassName={styles.nextOverride}
                    onBack={() => hideModal()}
                    onNext={() => placeOrder()}
                  />
                </Layout.Form>
              )}
            </>
          )}
        </Layout.Col>
      </Layout.Row>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isGuest: getUserIsGuest(state),
  userContext: getUserContext(state),
  multiSampleState: getMultiSampleState(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchUserContext: getUserContextAction,
  hideModal: () => dispatch(hideModalAction()),
  checkFreeSampleAvailability: (cardType: string) =>
    dispatch(checkFreeSampleAvailabilityAction(cardType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DigitalSTDFreeSampleModal);
